import React, { useCallback } from 'react'

import { useForm } from 'react-hook-form'
import {
  Button,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
} from 'ui'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch } from 'react-redux'
import { login, updateSession } from '../session'
import { handleNetworkError } from 'utils'

const loginSchema = yup.object({
  email: yup.string().required().email(),
  password: yup.string().required().min(7),
})
export type Login = yup.InferType<typeof loginSchema>

/**
 * A simple login form for existing users.
 */
export const LoginForm: React.FC = () => {
  const dispatch = useDispatch<any>()

  const form = useForm<Login>({
    mode: 'onBlur',
    resolver: yupResolver(loginSchema as any) as any,
  })

  const handleFormSubmission = useCallback(
    async ({ email, password }: Login) => {
      return await handleNetworkError<any>(async () => {
        const { accessToken, refreshToken, errors, ...rest } = await login(
          import.meta.env.VITE_EPC_API_URL ??
            import.meta.env.REACT_APP_API_URL ??
            '',
          {
            email: email as string,
            password: password as string,
          }
        )
        if (accessToken && refreshToken) {
          dispatch(updateSession({ accessToken, refreshToken }))
        }
        return errors ?? []
      }, import.meta.env.VITE_EPC_API_URL ?? '')
    },
    [dispatch]
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmission)} className="mt-8">
        <div className="flex flex-col">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="my@emailaddress.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full mt-4">
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            variant="default"
            className="ml-auto mt-4"
          >
            {form.formState.isSubmitting ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>Login</span>
          </Button>
        </div>
      </form>
    </Form>
  )
}
